import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Fade from "@mui/material/Fade";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

// Material Kit 2 React examples
import HorizontalTeamCard from "examples/Cards/TeamCards/HorizontalTeamCard";

import Services from "pages/LandingPages/Home/sections/Services";
import DefaultNavbarLayout from "layouts/defaultNavbar";
import Footer from "layouts/footer";

// Images
import whatsappVentas from "assets/images/whatsapp-ventas.png";
import whatsappServicios from "assets/images/whatsapp-servicios.png";
import Products from "./sections/Products";
import Networks from "./sections/Networks";

const slides = [
  {
    image: require("assets/images/carousel/creta.jpg"),
    title: "Nueva Creta",
    subtitle: "Pasá al siguiente nivel",
    link: "/product/creta",
  },
  {
    image: require("assets/images/carousel/hb20.jpg"),
    title: "HB20",
    subtitle: "Otra categoría de auto",
    link: "/product/hb20",
  },
  {
    image: require("assets/images/carousel/tucson.jpg"),
    title: "New Tucson",
    subtitle: "Sobrepasa lo ordinario",
    link: "/product/tucson",
  },
  {
    image: require("assets/images/carousel/staria.jpg"),
    title: "All-new STARIA",
    subtitle: "Un salto al espacio",
    link: "/product/staria",
  },
];

function Home() {
  const [current, setCurrent] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const nextSectionRef = useRef(null);
  const navigate = useNavigate();

  const nextSlide = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
      setFadeIn(true);
    }, 300);
  };
  const prevSlide = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
      setFadeIn(true);
    }, 300);
  };

  const scrollToNextSection = () => {
    if (nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <DefaultNavbarLayout />
      <MKBox
        position="relative"
        display="flex"
        alignItems="flex-start"
        minHeight="100vh"
        pt={18}
        sx={{ overflow: "hidden" }}
      >
        {/* Imagen de fondo con fade */}
        <Fade in={fadeIn} timeout={600} key={current}>
          <MKBox
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            zIndex={0}
            sx={{
              backgroundImage: ({
                palette: { gradients },
                functions: { linearGradient, rgba },
              }) =>
                `${linearGradient(
                  rgba(gradients.dark.main, 0.5),
                  rgba(gradients.dark.state, 0.1)
                )}, url(${slides[current].image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transition: "background-image 0.6s cubic-bezier(0.4,0,0.2,1)",
            }}
          />
        </Fade>
        {/* Contenido textual sobre la imagen */}
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Grid
            container
            item
            xs={12}
            md={7}
            lg={6}
            flexDirection="column"
            justifyContent="center"
          >
            <MKTypography
              variant="h1"
              color="white"
              mb={3}
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              {slides[current].title}
            </MKTypography>
            <MKTypography
              variant="body1"
              color="white"
              opacity={0.8}
              pr={6}
              mr={6}
            >
              {slides[current].subtitle}
            </MKTypography>
            <Stack direction="row" spacing={1} mt={3}>
              <MKButton color="info" onClick={() => navigate(slides[current].link)}>
                Más información
              </MKButton>
            </Stack>
          </Grid>
        </Container>
        {/* Flechas de navegación */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: "absolute",
            top: "50%",
            left: 10,
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.55)",
            zIndex: 2,
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: "absolute",
            top: "50%",
            right: 10,
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.55)",
            zIndex: 2,
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
        {/* Flecha hacia abajo para pasar a la siguiente sección */}
        <IconButton
          onClick={scrollToNextSection}
          sx={{
            position: "absolute",
            bottom: 100,
            left: "50%",
            transform: "translateX(-50%)",
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.55)",
            zIndex: 2,
            animation: "bounce 2s infinite",
          }}
        >
          <KeyboardArrowDownIcon fontSize="large" />
        </IconButton>
        <style>{`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {transform: translateY(0) translateX(-50%);}
            40% {transform: translateY(10px) translateX(-50%);}
            60% {transform: translateY(5px) translateX(-50%);}
          }
        `}</style>
      </MKBox>
      {/* Siguiente sección */}
      <div ref={nextSectionRef} />
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) =>
            rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Products />

        <MKBox
          component="section"
          variant="gradient"
          bgColor="dark"
          position="relative"
          py={6}
          px={{ xs: 2, lg: 0 }}
          mx={-2}
        >
          <Container>
            <Grid container>
              <Grid item xs={12} md={8} sx={{ mb: 6 }}>
                <MKTypography variant="h3" color="white">
                  Contacta por whatsapp
                </MKTypography>
                <MKTypography variant="body2" color="white" opacity={0.8}>
                  Escribe por whatsapp para más información sobre nuestros vehículos y servicios. 
                  <br />Nuestro equipo está listo para ayudarte a encontrar el auto perfecto para ti.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <MKBox mb={1}>
                  <HorizontalTeamCard
                    image={whatsappVentas}
                    name="Ventas"
                    position={{ color: "info", label: "" }}
                    description="011 5939-3230"
                    link="https://seoul.com.ar/track.php?c=whatsapp&p=ventas&o=web"
                  />
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox mb={1}>
                  <HorizontalTeamCard
                    image={whatsappServicios}
                    name="Servicio técnico"
                    position={{ color: "info", label: "" }}
                    description="011 3468-3455"
                    link="https://seoul.com.ar/track.php?c=whatsapp&p=service&o=web"
                  />
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <Services />
        <Networks />
      </Card>
      <Footer />
    </>
  );
}

export default Home;
